import { Component, type ErrorInfo, type ReactNode } from 'react';
import { createLogger } from '@sorrem/shared';
import { reportClientCrash } from '@sorrem-client/ui/telemetry';
import { Button } from '@sorrem/ui';
import styles from './ErrorBoundary.module.scss';

const logger = createLogger('client:error-boundary');

export interface ErrorBoundaryProps {
    readonly children: ReactNode;
    /** Identifies which part of the app this boundary guards, used in logging and the default fallback copy. */
    readonly label: string;
    /** Renders in place of children once an error is caught. Receives the error and a callback that clears it and re-renders children. */
    readonly fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
    readonly error: Error | null;
}

/**
 * Catches render/lifecycle errors thrown by its descendants so one bad
 * component doesn't unmount the entire React tree with no fallback UI.
 * Does not catch errors from event handlers, effects, or async code -
 * those still need their own try/catch (this is a React limitation, not
 * a gap in this component).
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    public state: ErrorBoundaryState = { error: null };

    public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        logger.error(`[${this.props.label}] render error:`, error, errorInfo.componentStack ?? '');
        reportClientCrash({
            kind: 'render',
            message: `[${this.props.label}] ${error.message}`,
            stack: [error.stack, errorInfo.componentStack].filter(Boolean).join('\n'),
        });
    }

    private readonly reset = (): void => {
        this.setState({ error: null });
    };

    public render(): ReactNode {
        const { error } = this.state;
        if (error === null) {
            return this.props.children;
        }

        if (this.props.fallback) {
            return this.props.fallback(error, this.reset);
        }

        return (
            <div className={styles.errorBoundary} role="alert">
                <p className={styles.title}>Something went wrong</p>
                <p className={styles.message}>{this.props.label} ran into an unexpected error.</p>
                <Button variant="secondary" onClick={this.reset}>
                    Try again
                </Button>
            </div>
        );
    }
}
